import React, { useCallback } from 'react';
import Popover from '@mui/material/Popover';
import { Stack } from '@mui/material';
import { anchorRef, bindTrigger } from 'material-ui-popup-state/hooks';

import { FilterButton, SearchForm, TableContainer, TableHeader } from '@occc/ui-design-system';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetMessagingCampaignsList } from '@occc-app/dataAccessLayer/messagingCenter/queries';
import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';

import { MessagingCampaignsTable } from './components/messaging-campaigns-table.component';
import { useMessagingCampaignsFilter } from './features/table-filter/use-messaging-campaigns-filter.hook';
import { MessagingCampaignsFilterContainer } from './features/table-filter/messaging-campaigns-filter.container';
import { MessagingCampaignsFilterFormValues } from './features/table-filter/messaging-campaigns-filter.types';

import { MessagingCampaignArchiveConfirmDialog } from '../messaging-campaign-archive/messaging-campaign-archive-confirm-dialog.component';
import { CAMPAIGN_LIST_PAGE_TITLE } from '../../messaging-center.constants';
import { useMessagingCampaignArchiver } from '../messaging-campaign-archive/use-messaging-campaign-archiver';

export const MessagingCampaignsTableContainer = () => {
  const [paginationInfo, onPageChange] = usePaginationParams();

  const filterContainer = useMessagingCampaignsFilter();

  const { loading, error, data, paginationProps } = useGetMessagingCampaignsList({
    filter: filterContainer.queryFilter,
    paginationInfo,
    onPageChange,
  });

  const archiver = useMessagingCampaignArchiver();

  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.MESSAGING_CENTER_EDIT);
  const isUserPermittedToArchive = useIsUserPermittedTo(Feature.MESSAGING_CENTER_ARCHIVE);

  const onSubmitSearchForm = useCallback(
    (values: MessagingCampaignsFilterFormValues) => {
      const { search: prevSearch, ...prevFilter } = filterContainer.value;
      const nextFilter = values.search ? { ...prevFilter, search: values.search } : prevFilter;
      filterContainer.onChange(nextFilter);
    },
    [filterContainer],
  );

  const renderActions = () => {
    return (
      <Stack spacing={1} direction='row' alignItems='center'>
        <SearchForm
          onSubmit={onSubmitSearchForm}
          initialValues={{ search: filterContainer.value.search || '' }}
        />
        <FilterButton
          {...bindTrigger(filterContainer.popupState)}
          ref={anchorRef(filterContainer.popupState)}
          sx={{ ml: null, mr: null }}
          badgeSx={{
            '& .MuiBadge-dot': { top: '10px', right: '6px' },
          }}
          showBadge={filterContainer.isActive}
        />
      </Stack>
    );
  };

  return (
    <>
      <Popover
        {...filterContainer.popoverProps}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        keepMounted={false}
        sx={{
          '.MuiPopover-paper': {
            maxWidth: 460,
            minWidth: 300,
          },
        }}
      >
        <MessagingCampaignsFilterContainer
          value={filterContainer.value}
          onChange={filterContainer.onChange}
        />
      </Popover>
      <TableContainer>
        <TableHeader title={CAMPAIGN_LIST_PAGE_TITLE} actions={renderActions()} />
        <MessagingCampaignsTable
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          campaigns={data}
          isUserPermittedToArchive={isUserPermittedToArchive}
          isUserPermittedToEdit={isUserPermittedToEdit}
          onArchiveClick={archiver.archive}
        />
      </TableContainer>

      {archiver.isConfirming && (
        <MessagingCampaignArchiveConfirmDialog
          onClose={archiver.onCancel}
          onConfirm={archiver.onConfirm}
          loading={archiver.isLoading}
        />
      )}
    </>
  );
};
